<!-- src/components/CartTabs.vue -->
<template>
  <div class="bg-gray-100 min-h-screen p-4">
    <!-- Tabs -->
    <div class="flex mb-4 rounded overflow-hidden shadow max-w-md mx-auto">
      <button
        v-for="tab in tabs"
        :key="tab"
        @click="activeTab = tab"
        :class="[
          'flex-1 px-4 py-2 text-sm font-medium text-center',
          activeTab === tab
            ? 'bg-gray-500 text-white'
            : 'bg-white text-black border border-gray-300 hover:bg-gray-100'
        ]"
      >
        {{ getTabName(tab) }}
      </button>
    </div>

    <!-- Cart Content -->
    <div
      v-if="activeTab === 'cart'"
      class="bg-white rounded shadow p-4 w-full max-w-md mx-auto"
    >
      <div v-if="loading">⏳ กำลังโหลด...</div>
      <div v-else-if="cartItems.length === 0" class="text-center text-gray-400">
        ไม่มีสินค้าในตะกร้า
      </div>
      <div v-else>
        <CartItemDisplay
          v-for="item in cartItems"
          :key="item.cart_id"
          :item="item"
          view-type="cart"
        />
        <div class="text-sm font-bold text-right mt-4 text-gray-800">
          รวมทั้งหมด: ฿{{ totalPrice.toLocaleString() }}
        </div>
      </div>
    </div>

    <!-- Favorite Content -->
    <div
      v-else-if="activeTab === 'favorite'"
      class="bg-white rounded shadow p-4 w-full max-w-md mx-auto"
    >
      <div v-if="loading">⏳ กำลังโหลด...</div>
      <div v-else-if="cartItems.length === 0" class="text-center text-gray-400">
        ❤️ ยังไม่มีรายการโปรด
      </div>
      <div v-else>
        <CartItemDisplay
          v-for="fav in cartItems"
          :key="fav.promo_id || fav.cart_id"
          :item="fav"
          view-type="favorite"
        />
      </div>
    </div>

    <!-- Order Content -->
    <div
      v-else-if="activeTab === 'order'"
      class="bg-white rounded shadow p-4 w-full max-w-md mx-auto"
    >
      <div v-if="loading">⏳ กำลังโหลด...</div>
      <div v-else-if="orders.length === 0" class="text-center text-gray-400">
        🧾 ยังไม่มีคำสั่งซื้อ
      </div>
      <div v-else>
        <OrderItemDisplay v-for="order in orders" :key="order.order_id" :order="order" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import OrderItemDisplay from "./OrderItemDisplay.vue";
import CartItemDisplay from "./CartItemDisplay.vue";

export default {
  name: "CartTabs",
  components: {
    OrderItemDisplay,
    CartItemDisplay,
  },
  props: {
    user: Object, // Prop ที่รับข้อมูล user มาจากคอมโพเนนต์แม่ (ควรมี user_id)
  },
  data() {
    return {
      tabs: ["cart", "favorite", "order"],
      activeTab: "cart", // แท็บที่ใช้งานอยู่เริ่มต้น
      cartItems: [], // เก็บข้อมูลตะกร้าสินค้า หรือรายการโปรด
      orders: [], // เก็บข้อมูลคำสั่งซื้อ
      loading: true, // สถานะการโหลดข้อมูล
      // *** สำคัญมาก: เปลี่ยน BASE_URL เป็น URL จริงของ API ของคุณ ***
      BASE_URL: 'https://dev-bank.indigoapp.com', // ตัวอย่าง: 'https://api.yourwebsite.com'
    };
  },
  computed: {
    totalPrice() {
      return this.cartItems.reduce((sum, item) => {
        const price = parseFloat(item.selling_price) || 0;
        const qty = parseInt(item.quantity) || 0;
        return sum + price * qty;
      }, 0);
    },
  },
  watch: {
    // Watcher สำหรับ activeTab: เมื่อแท็บเปลี่ยน ให้เรียก fetchData ใหม่
    activeTab: {
      immediate: true, // ทำให้เรียก fetchData ทันทีเมื่อคอมโพเนนต์ถูกสร้างครั้งแรก
      handler(newTab) {
        if (this.user?.user_id) { // ตรวจสอบว่า user_id มีค่าก่อนเรียก API
          this.fetchData(newTab);
        }
      }
    },
    // Watcher สำหรับ user prop: เมื่อ user object เปลี่ยน (เช่น user_id เปลี่ยน) ให้เรียก fetchData ใหม่
    user: {
      immediate: true, // ทำให้เรียก fetchData ทันทีเมื่อคอมโพเนนต์ถูกสร้างครั้งแรก
      handler(newUser) {
        if (newUser?.user_id && this.activeTab) { // ตรวจสอบ user_id และ activeTab ก่อนเรียก
          this.fetchData(this.activeTab);
        }
      },
    },
  },
  methods: {
    // เมธอดสำหรับแสดงชื่อแท็บเป็นภาษาไทย
    getTabName(tab) {
      const tabNames = {
        cart: "ตะกร้าสินค้า",
        favorite: "รายการโปรด",
        order: "คำสั่งซื้อ",
      };
      return tabNames[tab];
    },
    // เมธอดหลักสำหรับดึงข้อมูลจาก API (รวม logic ของ cart, favorite, order)
    async fetchData(currentViewType) {
      this.loading = true; // ตั้งสถานะโหลดเป็น true
      try {
        let endpoint;
        if (currentViewType === "order") {
          // Endpoint สำหรับคำสั่งซื้อ: /api/orders/:user_id
          endpoint = `${this.BASE_URL}/api/orders/user/${this.user.user_id}`;
        } else {
          // Endpoint สำหรับตะกร้าสินค้าและรายการโปรด: /api/cart/:userId?type={cart|favorite}
          endpoint = `${this.BASE_URL}/api/cart/user/${this.user.user_id}?type=${currentViewType}`;
        }

        const response = await axios.get(endpoint);

        // กำหนดข้อมูลตาม viewType ที่กำลังใช้งาน
        if (currentViewType === "order") {
          this.orders = response.data;
        } else {
          // สำหรับ 'cart' และ 'favorite' เราจะใช้ cartItems เดียวกัน
          this.cartItems = response.data;
        }
      } catch (error) {
        console.error(`❌ เกิดข้อผิดพลาดในการดึงข้อมูล (${currentViewType}):`, error);
        // เคลียร์ข้อมูลที่เกี่ยวข้องหากเกิดข้อผิดพลาด
        if (currentViewType === "order") {
          this.orders = [];
        } else {
          this.cartItems = [];
        }
      } finally {
        this.loading = false; // ตั้งสถานะโหลดเป็น false เมื่อดึงข้อมูลเสร็จสิ้น (ไม่ว่าจะสำเร็จหรือล้มเหลว)
      }
    },
  },
};
</script>

<style scoped>
/* คุณสามารถเพิ่ม CSS ที่นี่ได้ หากต้องการ */
</style>